// PixiJS v8 compiles shader programs via new Function() / eval() by default,
// which Telegram Mini App's CSP blocks with 'unsafe-eval'. This sub-entry
// swaps in the pre-built code paths that don't need runtime eval. MUST be
// imported BEFORE any 'pixi.js' import in the bundle graph so the side-effect
// patch runs before Application/Renderer constructors run their unsafe-eval
// gate check.
import 'pixi.js/unsafe-eval'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import './i18n'
import './styles/global.css'
import { applyTelegramTheme } from './hooks/useTelegramTheme'
import { initSentryWeb } from './sentry'
import { initAnalytics } from './analytics'

/**
 * App bootstrap.
 *
 * We deliberately do NOT call `init()` from `@telegram-apps/sdk-react` here —
 * keeping it out of the launcher chunk saves ~30–50 KB gz. The legacy
 * `window.Telegram.WebApp` object (loaded via <script> in index.html) is
 * enough for ready/expand/theme/swipes. Routes that need the React hooks
 * import `@telegram-apps/sdk-react` lazily.
 */

async function bootstrapTelegram(): Promise<void> {
  // Fire Sentry init early (no await — telemetry shouldn't block boot).
  void initSentryWeb()
  // PostHog analytics — also lazy / fire-and-forget.
  void initAnalytics()

  const tg = window.Telegram?.WebApp
  if (!tg) {
    // Outside Telegram (local dev / web preview / hosted-preview QA).
    // In dev, dynamically load mockTelegramEnv from @telegram-apps/sdk-react
    // so the app behaves as if running inside a real WebApp host. The dynamic
    // import keeps the SDK out of the production launcher chunk.
    if (import.meta.env.DEV) {
      try {
        const mod = await import('@telegram-apps/sdk-react')
        if (typeof mod.mockTelegramEnv === 'function') {
          mod.mockTelegramEnv({
            launchParams: {
              tgWebAppVersion: '7.7',
              tgWebAppPlatform: 'tdesktop',
              tgWebAppThemeParams: { bg_color: '#ffffff', text_color: '#000000' },
              tgWebAppData: 'auth_date=0&hash=mock&user=%7B%22id%22%3A0%2C%22first_name%22%3A%22Dev%22%7D',
            },
          })
        }
      } catch {
        // SDK import failed in dev — fall through with the default theme.
      }
    }
    applyTelegramTheme({})
    return
  }

  try {
    tg.ready()
  } catch {
    /* no-op: ready() throws if called twice */
  }

  try {
    tg.expand()
  } catch {
    /* no-op */
  }

  // enableClosingConfirmation: ask user before they swipe-down-to-close.
  // Critical for mid-game lifecycle (Architect §5.2 prevention #2) —
  // without this, an accidental gesture loses the in-progress run silently.
  try {
    if (typeof tg.enableClosingConfirmation === 'function') {
      tg.enableClosingConfirmation()
    }
  } catch {
    /* no-op */
  }

  // Bot API 7.7 introduced disableVerticalSwipes. Gate via feature-check.
  if (
    typeof tg.disableVerticalSwipes === 'function' &&
    (typeof tg.isVersionAtLeast !== 'function' || tg.isVersionAtLeast('7.7'))
  ) {
    try {
      tg.disableVerticalSwipes()
    } catch {
      /* no-op */
    }
  }

  applyTelegramTheme(tg.themeParams ?? {})

  // Watchdog: some clients silently re-enable vertical swipes after navigation
  // or focus changes. Re-disable every 10s, but only if the API exists.
  // CR-M5 fixes:
  //   1. Save the handle and clear on `pagehide` so we don't leak the timer
  //      across SPA navigations / bfcache restores.
  //   2. Strict `=== true`: undefined (older clients without the getter)
  //      previously satisfied `!== false` and re-disabled redundantly, which
  //      could double-invoke a non-idempotent shim on some hosts.
  if (typeof tg.disableVerticalSwipes === 'function') {
    const watchdogHandle = window.setInterval(() => {
      try {
        if (tg.isVerticalSwipesEnabled === true) {
          tg.disableVerticalSwipes?.()
        }
      } catch {
        /* no-op */
      }
    }, 10_000)
    window.addEventListener(
      'pagehide',
      () => {
        window.clearInterval(watchdogHandle)
      },
      { once: true },
    )
  }

  // Theme change listener (Telegram fires `themeChanged`).
  try {
    tg.onEvent('themeChanged', () => {
      applyTelegramTheme(tg.themeParams ?? {})
    })
  } catch {
    /* no-op */
  }
}

// Fire-and-forget: mockTelegramEnv dev fallback can take a microtask hop,
// but React mount shouldn't block on it.
void bootstrapTelegram()

const rootEl = document.getElementById('root')
if (!rootEl) {
  throw new Error('Missing <div id="root"> in index.html')
}

createRoot(rootEl).render(
  <StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </StrictMode>,
)
